import React from 'react';
import Icon from '../../../components/AppIcon';

const TerritoryOverview = ({ agent }) => {
  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Territory Performance</h3>
          <Icon name="Globe" size={20} className="text-primary" />
        </div>

        {/* Territory Cards */}
        <div className="space-y-4">
          {agent?.territories_details?.map((territory, index) => (
            <div key={territory?.id} className="border border-border rounded-lg p-4">
              <div className="space-y-3">
                {/* Territory Header */}
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-foreground">{territory?.name}</h4>
                  <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                    <Icon name="TrendingUp" size={14} />
                    <span>{territory?.performance}%</span>
                  </div>
                </div>

                {/* Performance Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Performance Score</span>
                    <span className="font-medium text-foreground">{territory?.performance}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        territory?.performance >= 90 ? 'bg-success' :
                        territory?.performance >= 75 ? 'bg-primary' :
                        territory?.performance >= 60 ? 'bg-warning' : 'bg-error'
                      }`}
                      style={{ width: `${territory?.performance}%` }}
                    />
                  </div>
                </div>

                {/* Territory Stats */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground">Volume</div>
                    <div className="font-medium text-foreground">
                      ${territory?.volume?.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Commission</div>
                    <div className="font-medium text-success">
                      ${territory?.commission?.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Performance Indicator */}
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={territory?.performance >= 90 ? "CheckCircle" : 
                          territory?.performance >= 75 ? "AlertCircle" : "XCircle"} 
                    size={14} 
                    className={
                      territory?.performance >= 90 ? 'text-success' :
                      territory?.performance >= 75 ? 'text-warning' : 'text-error'
                    } 
                  />
                  <span className={`text-xs ${
                    territory?.performance >= 90 ? 'text-success' :
                    territory?.performance >= 75 ? 'text-warning' : 'text-error'
                  }`}>
                    {territory?.performance >= 90 ? 'Excellent Performance' :
                     territory?.performance >= 75 ? 'Good Performance' : 'Needs Improvement'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Territory Summary */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="BarChart3" size={16} />
            <span>Combined Territory Stats</span>
          </h4>
          
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Territories:</span>
              <span className="font-medium text-foreground">{agent?.territories?.length}</span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Combined Volume:</span>
              <span className="font-medium text-foreground">
                ${agent?.territories_details?.reduce((sum, t) => sum + t?.volume, 0)?.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Commission:</span>
              <span className="font-medium text-success">
                ${agent?.territories_details?.reduce((sum, t) => sum + t?.commission, 0)?.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Avg Performance:</span>
              <span className="font-medium text-primary">
                {Math.round(agent?.territories_details?.reduce((sum, t) => sum + t?.performance, 0) / agent?.territories_details?.length)}%
              </span>
            </div>
          </div>
        </div>

        {/* Territory Expansion Opportunity */}
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="MapPin" size={20} className="text-primary mt-0.5" />
            <div>
              <h4 className="font-semibold text-primary mb-1">Expansion Opportunity</h4>
              <p className="text-sm text-primary/80 mb-3">
                Based on your strong performance, you're eligible to expand into additional territories.
              </p>
              <div className="space-y-2 text-xs text-primary/70">
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={12} />
                  <span>Asia Pacific territory now available</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={12} />
                  <span>Up to 4.2% commission rate</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Check" size={12} />
                  <span>Growing market with high demand</span>
                </div>
              </div>
              <button className="mt-3 text-xs font-medium text-primary hover:text-primary/80 underline">
                Learn More About Expansion
              </button>
            </div>
          </div>
        </div>

        {/* Performance Tips */}
        <div className="space-y-2">
          <h4 className="font-semibold text-foreground text-sm">Performance Tips</h4>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex items-start space-x-2">
              <Icon name="Lightbulb" size={12} className="mt-0.5 text-warning" />
              <span>Focus marketing efforts on underperforming territories</span>
            </div>
            <div className="flex items-start space-x-2">
              <Icon name="Lightbulb" size={12} className="mt-0.5 text-warning" />
              <span>Consider local partnerships to boost regional presence</span>
            </div>
            <div className="flex items-start space-x-2">
              <Icon name="Lightbulb" size={12} className="mt-0.5 text-warning" />
              <span>Analyze seasonal trends for better resource allocation</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerritoryOverview;